












































































































import Vue, { PropType } from "vue";
import { Option } from "@/types";
import { mapState } from "vuex";

const findFocus = (): HTMLElement => {
  const focusPoint = document.activeElement;
  return focusPoint as HTMLElement;
};

export default Vue.extend({
  props: {
    uniqueSelectorId: String,
    selectorLabel: String,
    options: { type: Array as PropType<Option[]> },
    initialOption: { type: Object as PropType<Option> }
  },
  data() {
    return {
      filteredOptions: [] as Option[],
      showOptions: false as boolean,
      selectedOption: undefined as Option | undefined,
      selectorState: "initial" as string,
      filterInputValue: "" as string,
      selectInputValue: "" as string,
      initialized: false as boolean
    };
  },
  computed: mapState(["lang"]),
  watch: {
    options(newOptions: Option[], oldOptions: Option[]) {
      this.filteredOptions = [...newOptions].sort(this.optionsSort);
      // update label of the selected option by new options
      if (oldOptions && this.selectedOption) {
        const updatedSelectedOption = newOptions.find(
          (option) => option.id === this.selectedOption!.id
        );
        if (updatedSelectedOption) {
          this.selectedOption = updatedSelectedOption;
          this.selectInputValue = updatedSelectedOption.label[this.lang];
        }
      }
    }
  },
  methods: {
    async initializeOptions() {
      this.selectInputValue = this.initialOption.label[this.lang];
      this.selectedOption = this.initialOption;
      this.$emit("selected-option", this.selectedOption);
      this.filteredOptions = [...this.options].sort(this.optionsSort);
      this.initialized = true;
    },
    optionsSort(a: Option, b: Option) {
      if (a.showFirst) {
        return -1;
      } else if (b.showFirst) {
        return 1;
      }
      return a.label[this.lang].localeCompare(b.label[this.lang]);
    },
    filterOptions(): void {
      this.filteredOptions = this.options
        .filter((o) => {
          return o.label[this.lang]
            .toLowerCase()
            .includes(this.filterInputValue.toLowerCase());
        })
        .sort(this.optionsSort);
    },
    getOptionByid(id: string): Option | undefined {
      return this.options.find((o) => o.id === id);
    },
    toggleSelectorOpen(open: boolean | undefined = undefined) {
      if (open !== undefined) {
        this.showOptions = open;
        // this.selectedOption is defined always after initialization
        if (!open && this.selectedOption) {
          this.filterInputValue = "";
          // set previosly selected value to input if exiting selector
          this.selectInputValue = this.selectedOption.label[this.lang];
          this.filteredOptions = [...this.options].sort(this.optionsSort);
        }
      } else {
        this.showOptions = !this.showOptions;
        if (!this.showOptions) {
          this.filterInputValue = "";
        }
      }
    },
    setState(state: string) {
      this.selectorState = state;
    },
    getInputElement(): HTMLElement {
      return this.$refs["selectInput-" + this.uniqueSelectorId] as HTMLElement;
    },
    handleSelectorClick() {
      if (!this.initialized) {
        return;
      }
      const currentFocus = findFocus();
      switch (this.selectorState) {
        case "initial":
          this.toggleSelectorOpen(true);
          this.setState("opened");
          break;
        case "opened":
          if (currentFocus === this.getInputElement()) {
            this.toggleSelectorOpen(false);
            this.setState("initial");
          } else if (currentFocus && currentFocus.tagName === "LI") {
            this.makeChoice(currentFocus);
            this.toggleSelectorOpen(false);
            this.setState("closed");
          }
          break;
        case "filtered": // i.e. option was selected
          if (currentFocus && currentFocus.tagName === "LI") {
            this.makeChoice(currentFocus);
            this.toggleSelectorOpen(false);
            this.setState("closed");
          } else {
            this.toggleSelectorOpen();
            this.setState(this.showOptions ? "opened" : "closed");
          }
          break;
        case "closed":
          this.toggleSelectorOpen(true);
          this.setState("filtered");
          break;
      }
    },
    getOptionIdFromElement(element: HTMLElement): string {
      return element!.querySelector("span")!.textContent!.trim();
    },
    makeChoice(whichOption: HTMLElement) {
      // read option identifier from hidden span element
      const optionId = this.getOptionIdFromElement(whichOption);
      const option = this.getOptionByid(optionId);

      if (option) {
        this.setSelectedOption(option, true);
      } else {
        console.log("Could not select:", optionId);
      }
    },
    setSelectedOption(option: Option, focusInput: boolean) {
      this.selectedOption = option;
      this.selectInputValue = option.label[this.lang];
      this.$emit("selected-option", option);
      if (focusInput) {
        this.moveFocus(findFocus(), "input");
      }
    },
    getOptionAtIndex(index: number): HTMLLIElement {
      const option = this.filteredOptions[index];
      // @ts-ignore
      return this.$refs["options-" + this.uniqueSelectorId + option.id][0] as HTMLLIElement;
    },
    moveFocus(fromHere: HTMLElement, toThere: string) {
      if (toThere === "input" || this.filteredOptions.length == 0) {
        this.getInputElement().focus();
        return;
      }
      switch (fromHere) {
        case this.getInputElement():
          if (toThere === "forward") {
            this.getOptionAtIndex(0).focus();
          } else if (toThere === "back") {
            this.getOptionAtIndex(this.filteredOptions.length - 1).focus();
          }
          break;
        case this.getOptionAtIndex(0):
          if (toThere === "forward" && this.filteredOptions[1]) {
            this.getOptionAtIndex(1).focus();
          } else if (toThere === "back") {
            this.getInputElement().focus();
          }
          break;
        case this.getOptionAtIndex(this.filteredOptions.length - 1):
          if (toThere === "forward") {
            this.getOptionAtIndex(0).focus();
          } else if (toThere === "back") {
            this.getOptionAtIndex(this.filteredOptions.length - 2).focus();
          }
          break;
        default: {
          const currentItem = findFocus();
          const selectedValue = currentItem ? this.getOptionIdFromElement(currentItem) : "";
          const indexOfSelectedValue = this.filteredOptions
            .map((o) => o.id)
            .indexOf(selectedValue);
          if (toThere === "forward") {
            this.getOptionAtIndex(indexOfSelectedValue + 1).focus();
          } else if (toThere === "back" && indexOfSelectedValue > 0) {
            this.getOptionAtIndex(indexOfSelectedValue - 1).focus();
          } else {
            this.getInputElement().focus();
          }
          break;
        }
      }
    },
    preventKeyDownScroll(e: KeyboardEvent) {
      const code = e.keyCode ? e.keyCode : e.code;
      switch (code) {
        case 38:
        case 40: // Arrow keys
          e.preventDefault();
          break;
        default:
          break; // do not block other keys
      }
    },
    handleKeyDown(whichKey: KeyboardEvent) {
      this.preventKeyDownScroll(whichKey);
      if (whichKey.code === "Tab") {
        this.toggleSelectorOpen(false);
        this.setState("closed");
        return;
      }
      const currentFocus = findFocus();
      switch (whichKey.code) {
        case "Enter":
          if (this.selectorState === "initial") {
            this.toggleSelectorOpen(true);
            this.setState("opened");
          } else if (
            this.selectorState === "opened" &&
            currentFocus &&
            currentFocus.tagName === "LI"
          ) {
            this.makeChoice(currentFocus);
            this.toggleSelectorOpen(false);
            this.setState("closed");
          } else if (
            this.selectorState === "opened" &&
            currentFocus === this.getInputElement()
          ) {
            this.toggleSelectorOpen(false);
            this.setState("closed");
          } else if (
            this.selectorState === "filtered" &&
            currentFocus &&
            currentFocus.tagName === "LI"
          ) {
            this.makeChoice(currentFocus);
            this.toggleSelectorOpen(false);
            this.setState("closed");
          } else if (
            this.selectorState === "filtered" &&
            currentFocus === this.getInputElement()
          ) {
            this.toggleSelectorOpen(true);
            this.setState("opened");
          } else {
            this.toggleSelectorOpen(true);
            this.setState("filtered");
          }
          break;

        case "Escape":
          if (this.selectorState === "opened" || this.selectorState === "filtered") {
            this.toggleSelectorOpen(false);
            this.setState("initial");
          }
          break;

        case "ArrowDown":
          if (this.selectorState === "initial" || this.selectorState === "closed") {
            this.toggleSelectorOpen(true);
            this.moveFocus(this.getInputElement(), "forward");
            this.setState("opened");
          } else {
            this.toggleSelectorOpen(true);
            this.moveFocus(currentFocus, "forward");
          }
          break;
        case "ArrowUp":
          if (this.selectorState === "initial" || this.selectorState === "closed") {
            this.toggleSelectorOpen(true);
            this.moveFocus(this.getInputElement(), "back");
            this.setState("opened");
          } else {
            this.moveFocus(currentFocus, "back");
          }
          break;
        default:
          // let's move focus back to input if it's somewhere else at normal (letter) key down
          // shift key should not cause this
          if (findFocus() !== this.getInputElement() && whichKey.keyCode !== 16) {
            this.moveFocus(findFocus(), "input");
          }
      }
    },
    handleKeyUp(whichKey: KeyboardEvent) {
      switch (whichKey.code) {
        case "Enter":
        case "Escape":
        case "ArrowDown":
        case "ArrowUp":
          break;
        default:
          this.filterOptions();
          if (this.selectorState === "initial" || this.selectorState === "closed") {
            this.toggleSelectorOpen(true);
            this.setState("filtered");
          } else if (this.selectorState === "opened") {
            this.setState("filtered");
          }
      }
    }
  },
  mounted() {
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target && !target.closest("#select-container-" + this.uniqueSelectorId)) {
        this.toggleSelectorOpen(false);
        this.setState("initial");
      }
    });
    this.initializeOptions();
  }
});
